import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import styles from './FuturisticAdmin.module.css';

interface OverviewStats {
  totalUsers: number;
  verifiedUsers: number;
  unverifiedUsers: number;
  activeUsers: number;
  recentSignups: number;
  usersByPlan: Record<string, number>;
  usersByVideoType: Record<string, number>;
  usersWithVeoCredits: number;
}

interface BillingCheckResult {
  success: boolean;
  message: string;
  timestamp?: string;
  data?: {
    downgraded: number;
    verified: number;
    errors: number;
    downgradedUsers: Array<{
      email: string;
      fromPlan: string;
      toPlan: string;
    }>;
  };
}

const OverviewDashboard: React.FC = () => {
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [billingCheckLoading, setBillingCheckLoading] = useState(false);
  const [billingCheckResult, setBillingCheckResult] = useState<BillingCheckResult | null>(null);

  useEffect(() => {
    loadOverviewStats();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  };

  const loadOverviewStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/admin/overview'), {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) throw new Error('Failed to load overview stats');
      const data = await response.json();
      setOverviewStats(data);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview stats');
    } finally {
      setLoading(false);
    }
  };

  const triggerBillingCheck = async () => {
    try {
      setBillingCheckLoading(true);
      setBillingCheckResult(null);
      
      const response = await fetch(getApiUrl('/billing/admin/check-unpaid-users'), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger billing check');
      }
      
      const responseData = await response.json();
      setBillingCheckResult({
        success: true,
        message: responseData.message || 'Billing check completed successfully',
        timestamp: new Date().toISOString(),
        data: responseData.data,
      });
      
      // Refresh overview stats to show updated plan counts
      setTimeout(() => {
        loadOverviewStats();
      }, 1000);
      
    } catch (err) {
      setBillingCheckResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to trigger billing check',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setBillingCheckLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ color: 'white', textAlign: 'center', padding: '4rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
        <p>Loading overview statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'white', padding: '2rem' }}>
        <div className={styles.card} style={{ 
          backgroundColor: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.3)' 
        }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <h3 style={{ color: '#ef4444', margin: '0 0 1rem 0' }}>Error Loading Overview</h3>
            <p style={{ color: '#e2e8f0', margin: '0 0 1.5rem 0' }}>{error}</p>
            <button 
              onClick={loadOverviewStats}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!overviewStats) {
    return null;
  }

  return (
    <div style={{ color: 'white', padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 className={styles.testTitle}>📊 System Overview</h1>
        <p style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '1rem' }}>
          Comprehensive platform statistics and insights
        </p>
        
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            onClick={loadOverviewStats}
            style={{
              backgroundColor: 'rgba(6, 182, 212, 0.2)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              color: '#06b6d4',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>

          <button 
            onClick={triggerBillingCheck}
            disabled={billingCheckLoading}
            style={{
              backgroundColor: billingCheckLoading 
                ? 'rgba(100, 116, 139, 0.2)' 
                : 'rgba(239, 68, 68, 0.2)',
              border: billingCheckLoading 
                ? '1px solid rgba(100, 116, 139, 0.3)' 
                : '1px solid rgba(239, 68, 68, 0.3)',
              color: billingCheckLoading ? '#94a3b8' : '#ef4444',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: billingCheckLoading ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              opacity: billingCheckLoading ? 0.6 : 1
            }}
          >
            {billingCheckLoading ? '⏳ Checking...' : '💳 Check Unpaid Users'}
          </button>
        </div>

        {/* Billing Check Result */}
        {billingCheckResult && (
          <div 
            className={styles.card} 
            style={{ 
              marginTop: '1rem',
              backgroundColor: billingCheckResult.success 
                ? 'rgba(16, 185, 129, 0.1)' 
                : 'rgba(239, 68, 68, 0.1)',
              border: billingCheckResult.success 
                ? '1px solid rgba(16, 185, 129, 0.3)' 
                : '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
              <div style={{ fontSize: '2rem' }}>
                {billingCheckResult.success ? '✅' : '❌'}
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ 
                  color: billingCheckResult.success ? '#10b981' : '#ef4444',
                  margin: '0 0 0.5rem 0',
                  fontSize: '1rem'
                }}>
                  {billingCheckResult.success ? 'Billing Check Complete' : 'Billing Check Failed'}
                </h4>
                <p style={{ color: '#e2e8f0', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
                  {billingCheckResult.message}
                </p>
                <p style={{ color: '#64748b', fontSize: '0.75rem', margin: '0 0 1rem 0' }}>
                  {new Date(billingCheckResult.timestamp || '').toLocaleString()}
                </p>

                {/* Detailed Downgrade Information */}
                {billingCheckResult.success && billingCheckResult.data && (
                  <div style={{ marginTop: '1rem' }}>
                    {/* Summary Stats */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                      gap: '0.75rem',
                      marginBottom: '1rem'
                    }}>
                      <div style={{ 
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
                          {billingCheckResult.data.downgraded}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Downgraded</div>
                      </div>
                      <div style={{ 
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                          {billingCheckResult.data.verified}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Verified Active</div>
                      </div>
                      {billingCheckResult.data.errors > 0 && (
                        <div style={{ 
                          background: 'rgba(251, 146, 60, 0.1)',
                          border: '1px solid rgba(251, 146, 60, 0.3)',
                          borderRadius: '8px',
                          padding: '0.75rem',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fb923c' }}>
                            {billingCheckResult.data.errors}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Errors</div>
                        </div>
                      )}
                    </div>

                    {/* Downgraded Users List */}
                    {billingCheckResult.data.downgradedUsers && billingCheckResult.data.downgradedUsers.length > 0 && (
                      <div style={{ marginTop: '1rem' }}>
                        <h5 style={{ 
                          color: '#e2e8f0', 
                          fontSize: '0.9rem', 
                          marginBottom: '0.5rem',
                          fontWeight: 'bold'
                        }}>
                          📋 Downgraded Users:
                        </h5>
                        <div style={{ 
                          maxHeight: '200px', 
                          overflowY: 'auto',
                          background: 'rgba(15, 23, 42, 0.5)',
                          borderRadius: '6px',
                          padding: '0.75rem'
                        }}>
                          {billingCheckResult.data.downgradedUsers.map((user, index) => (
                            <div 
                              key={index}
                              style={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '0.5rem',
                                marginBottom: '0.5rem',
                                background: 'rgba(30, 41, 59, 0.5)',
                                borderRadius: '4px',
                                fontSize: '0.85rem'
                              }}
                            >
                              <span style={{ color: '#94a3b8', flex: 1 }}>{user.email}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ 
                                  color: '#ef4444',
                                  textTransform: 'uppercase',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}>
                                  {user.fromPlan.replace(/_/g, ' ')}
                                </span>
                                <span style={{ color: '#64748b' }}>→</span>
                                <span style={{ 
                                  color: '#10b981',
                                  textTransform: 'uppercase',
                                  fontSize: '0.75rem',
                                  fontWeight: 'bold'
                                }}>
                                  {user.toPlan}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setBillingCheckResult(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  padding: '0.25rem',
                  lineHeight: 1
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Key Metrics Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem' 
        }}>
          <div className={styles.card}>
            <h3 style={{ color: '#06b6d4', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Users</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{overviewStats.totalUsers}</p>
          </div>
          
          <div className={styles.card}>
            <h3 style={{ color: '#10b981', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Verified</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{overviewStats.verifiedUsers}</p>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {((overviewStats.verifiedUsers / overviewStats.totalUsers) * 100).toFixed(1)}% of total
            </p>
          </div>
          
          <div className={styles.card}>
            <h3 style={{ color: '#f59e0b', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Active (30d)</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{overviewStats.activeUsers}</p>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {((overviewStats.activeUsers / overviewStats.totalUsers) * 100).toFixed(1)}% active
            </p>
          </div>
          
          <div className={styles.card}>
            <h3 style={{ color: '#8b5cf6', fontSize: '0.9rem', marginBottom: '0.5rem' }}>New (7d)</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{overviewStats.recentSignups}</p>
          </div>

          <div className={styles.card}>
            <h3 style={{ color: '#ec4899', fontSize: '0.9rem', marginBottom: '0.5rem' }}>VEO Credits Users</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{overviewStats.usersWithVeoCredits}</p>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {((overviewStats.usersWithVeoCredits / overviewStats.totalUsers) * 100).toFixed(1)}% with credits
            </p>
          </div>
        </div>

        {/* Users by Plan */}
        <div className={styles.card}>
          <h3 style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '1rem', borderBottom: '1px solid rgba(51, 65, 85, 0.5)', paddingBottom: '0.5rem' }}>
            Users by Plan
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            {Object.entries(overviewStats.usersByPlan).map(([plan, count]) => {
              const percentage = ((count / overviewStats.totalUsers) * 100).toFixed(1);
              return (
                <div key={plan} style={{
                  padding: '1rem',
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(71, 85, 105, 0.3)',
                  borderRadius: '0.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>{plan}</span>
                    <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>{count}</span>
                  </div>
                  <div style={{ 
                    height: '8px', 
                    background: '#374151', 
                    borderRadius: '4px', 
                    overflow: 'hidden' 
                  }}>
                    <div style={{ 
                      width: `${percentage}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #06b6d4, #8b5cf6)',
                      borderRadius: '4px'
                    }}></div>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                    {percentage}% of all users
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Users by Video Type */}
        {Object.keys(overviewStats.usersByVideoType).length > 0 && (
          <div className={styles.card}>
            <h3 style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '1rem', borderBottom: '1px solid rgba(51, 65, 85, 0.5)', paddingBottom: '0.5rem' }}>
              Users by Video Type
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              {Object.entries(overviewStats.usersByVideoType)
                .sort(([, a], [, b]) => b - a)
                .map(([videoType, count]) => {
                  const totalWithVideoType = Object.values(overviewStats.usersByVideoType).reduce((a, b) => a + b, 0);
                  const percentage = ((count / totalWithVideoType) * 100).toFixed(1);
                  return (
                    <div key={videoType} style={{
                      padding: '1rem',
                      background: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(71, 85, 105, 0.3)',
                      borderRadius: '0.5rem',
                      textAlign: 'center'
                    }}>
                      <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#10b981', margin: '0 0 0.25rem 0' }}>
                        {count}
                      </p>
                      <p style={{ fontSize: '0.9rem', fontWeight: '600', margin: '0 0 0.25rem 0' }}>
                        {videoType}
                      </p>
                      <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: 0 }}>
                        {percentage}% frequency
                      </p>
                    </div>
                  );
                })}
            </div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '1rem', textAlign: 'center' }}>
              {overviewStats.totalUsers - Object.values(overviewStats.usersByVideoType).reduce((a, b) => a + b, 0)} users haven't selected a video type yet
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewDashboard;
